import { RejectedBannerIcon } from '@/assets/icons/info-icons/rejectedBanner';
import { Banner } from '@/components/basicComponents/banner';
import Button from '@/components/basicComponents/button';
import Input from '@/components/basicComponents/input';
import ModalLoader from '@/components/basicComponents/modal/modalLoader';
import ModalWrappedContent from '@/components/basicComponents/modal/modalWrappedContent';
import { formatUserPhoneNumber } from '@/components/settings-page/users/firmTable/tableBody';
import { useErrorMessages } from '@/hooks/i18n';
import { useAuth } from '@/hooks/use-auth';
import { useResponsive } from '@/hooks/use-responsive';
import { UPDATE_USER_MOBILE_PHONE_NUMBER_MUTATION } from '@/pages/Profile/queries';
import { themeVar } from '@/styles/theme';
import { useMutation } from '@apollo/client';
import { multiFactor, PhoneAuthProvider, PhoneMultiFactorGenerator, PhoneMultiFactorInfo } from 'firebase/auth';
import { Dispatch, SetStateAction, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import styled from 'styled-components';
import { TWO_FACTOR_AUTHENTICATION_STATUS_ENUM, TwoFactorAuthentication, TwoFactorAuthenticationDescription, TwoFactorAuthenticationTitle } from '..';

interface VerifyMobilePhoneNumberProps {
  mobileNumber: { countryCode: string; number: string };
  verificationId: string;
  sendVerificationCode: () => Promise<void>;
  handleStatusChange: (newStatus: TWO_FACTOR_AUTHENTICATION_STATUS_ENUM) => void;
  handleTwoFactorAuthenticationErrors: (error: any, setErrorMessage: Dispatch<SetStateAction<string | null>>) => void;
  changeMobileNumber?: boolean;
}

export const VerifyMobilePhoneNumber = ({
  mobileNumber,
  verificationId,
  sendVerificationCode,
  handleStatusChange,
  handleTwoFactorAuthenticationErrors,
  changeMobileNumber
}: VerifyMobilePhoneNumberProps) => {
  const { auth } = useAuth();
  const { isMobile } = useResponsive();
  const { default: defaultErrorMessage } = useErrorMessages();
  const { control, handleSubmit } = useForm<{ verificationCode: string }>({ defaultValues: { verificationCode: '' } });

  const [loading, setLoading] = useState(false);
  const [resendVerificationCodeLoading, setResendVerificationCodeLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [updateUserMobileNumber] = useMutation(UPDATE_USER_MOBILE_PHONE_NUMBER_MUTATION);

  const resendVerificationCode = async () => {
    try {
      setErrorMessage(null);
      setResendVerificationCodeLoading(true);

      await sendVerificationCode();
    } catch (error: any) {
      console.error(error);
      console.log(error.code);
      handleTwoFactorAuthenticationErrors(error, setErrorMessage);
    } finally {
      setResendVerificationCodeLoading(false);
    }
  };

  const onSave = async (data: { verificationCode: string }) => {
    try {
      setErrorMessage(null);
      setLoading(true);
      const { verificationCode } = data;

      if (verificationId && verificationCode) {
        const currentUser = auth?.currentUser;

        const multiFactorUser = multiFactor(currentUser);
        const multiFactorPhone = multiFactorUser.enrolledFactors.find((factor) => factor.factorId === 'phone') as PhoneMultiFactorInfo | undefined;

        // if the user currently has a phone number enrolled, unenroll it
        if (multiFactorUser && multiFactorPhone) {
          await multiFactorUser.unenroll(multiFactorPhone);
        }

        const cred = PhoneAuthProvider.credential(verificationId, verificationCode);
        const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(cred);

        await multiFactor(currentUser).enroll(multiFactorAssertion);

        if (changeMobileNumber) {
          await updateUserMobileNumber({
            variables: { data: { mobileNumber: { countryCode: mobileNumber.countryCode, number: mobileNumber.number } } }
          });
        }

        handleStatusChange(TWO_FACTOR_AUTHENTICATION_STATUS_ENUM.CONFIRMATION);
        return;
      }

      setErrorMessage(defaultErrorMessage);
    } catch (error: any) {
      console.error(error);
      console.log(error.code);
      handleTwoFactorAuthenticationErrors(error, setErrorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {resendVerificationCodeLoading && (
        <ModalWrappedContent isOpen={resendVerificationCodeLoading} title="Sending" showCancelButton={false} showCloseIcon={false} onClose={() => {}}>
          <ModalLoader />
        </ModalWrappedContent>
      )}
      <TwoFactorAuthentication>
        <VerifyPhoneImage src="/static/mock-images/verifyPhone.webp" alt="verify phone image" />
        <TwoFactorAuthenticationTitle>Verify Mobile Phone Number</TwoFactorAuthenticationTitle>
        {errorMessage && (
          <Banner
            icon={<RejectedBannerIcon fill={themeVar('context.light')} />}
            title="Error"
            description={errorMessage}
            bgColor={themeVar('context.error')}
          />
        )}
        <TwoFactorAuthenticationDescription>
          A verification code has been sent to your mobile phone number:{' '}
          <span style={{ fontWeight: 700 }}>{formatUserPhoneNumber(mobileNumber.countryCode, mobileNumber.number)}</span>{' '}
          <TwoFactorAuthenticationLink onClick={() => handleStatusChange(TWO_FACTOR_AUTHENTICATION_STATUS_ENUM.DEFAULT)}>
            Edit Phone Number
          </TwoFactorAuthenticationLink>
        </TwoFactorAuthenticationDescription>
        <Controller
          name="verificationCode"
          control={control}
          rules={{
            required: 'Verification code is required.',
            pattern: {
              value: /^\d{6}$/,
              message: 'Verification code must be a 6-digit number.'
            }
          }}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <Input
              label="Enter Verification Code"
              value={value || ''}
              onChange={(e) => {
                onChange(e.target.value);
              }}
              errorText={error?.message}
              size={isMobile ? 'md' : 'lg'}
              isRequiredField
            />
          )}
        />
      </TwoFactorAuthentication>
      <ButtonsWrapper>
        <Button size="md" styleType="outline" onClick={() => handleStatusChange(TWO_FACTOR_AUTHENTICATION_STATUS_ENUM.DEFAULT)}>
          Cancel
        </Button>
        <Button size="md" onClick={handleSubmit(onSave)} styleType={loading ? 'disabled' : 'default'} isLoading={loading}>
          Verify
        </Button>
      </ButtonsWrapper>
      <TwoFactorAuthenticationDescription>
        Not seeing your verification code? <TwoFactorAuthenticationLink onClick={resendVerificationCode}>Resend</TwoFactorAuthenticationLink>
      </TwoFactorAuthenticationDescription>
    </>
  );
};

const VerifyPhoneImage = styled.img`
  margin: 0 auto;
  width: 143px;
  height: 146px;
`;

const ButtonsWrapper = styled.div`
  display: flex;
  gap: 10px;
`;

const TwoFactorAuthenticationLink = styled.span`
  font-weight: 600;
  cursor: pointer;
  color: ${themeVar('action.primary')};
`;
